<template>
  <div class="product-showcase">
    <!-- ── Hero header ── -->
    <div class="showcase-hero">
      <div class="showcase-hero-content">
        <div class="showcase-hero-icon"><v-icon color="white" size="34">storefront</v-icon></div>
        <div>
          <h1 class="showcase-title">Marketplace Products</h1>
          <p class="showcase-sub">Browse, showcase and manage all PettyCash products in one place.</p>
        </div>
      </div>

      <div class="showcase-hero-actions">
        <v-btn
          small elevation="2"
          color="white"
          class="text-caption font-weight-bold text-none"
          rounded
          :to="{ name: 'marketplace.add_products' }"
        >
          <v-icon left size="16">add</v-icon>
          <span class="d-none d-sm-inline">Add Product</span>
          <span class="d-sm-none">New</span>
        </v-btn>
        <v-btn
          small elevation="2"
          color="white"
          class="text-caption font-weight-bold text-none"
          rounded
          :to="{ name: 'marketplace.products' }"
        >
          <v-icon left size="16">list_alt</v-icon>
          <span class="d-none d-sm-inline">Admin List</span>
          <span class="d-sm-none">Admin</span>
        </v-btn>
      </div>
    </div>

    <!-- ── Selection toolbar ── -->
    <v-slide-y-transition>
      <div v-if="selectedIds.length" class="showcase-toolbar">
        <div class="d-flex align-center flex-wrap" style="gap:10px">
          <v-checkbox
            :input-value="allSelected"
            :indeterminate="someSelected"
            dense hide-details class="mt-0 pt-0"
            @change="toggleAllSelected"
          ></v-checkbox>
          <span class="font-weight-bold" style="font-size:13px">
            {{ selectedIds.length }} selected
          </span>
          <v-spacer></v-spacer>
          <v-btn small rounded outlined color="teal" class="text-none font-weight-bold" @click="shareSelected">
            <v-icon left size="15">share</v-icon> Share
          </v-btn>
          <v-btn small rounded outlined color="teal" class="text-none font-weight-bold" @click="copySelected">
            <v-icon left size="15">content_copy</v-icon> Copy list
          </v-btn>
          <v-btn small rounded outlined color="grey" class="text-none font-weight-bold" @click="selectedIds = []">
            Clear
          </v-btn>
        </div>
      </div>
    </v-slide-y-transition>

    <!-- ── Filters ── -->
    <div class="showcase-filters">
      <v-text-field
        v-model="search"
        prepend-inner-icon="search"
        placeholder="Search products…"
        solo flat hide-details dense
        class="showcase-search"
        background-color="rgba(0,150,136,0.06)"
      ></v-text-field>

      <v-select
        v-model="category"
        :items="categories"
        item-text="name"
        item-value="name"
        label="Category"
        solo flat hide-details dense
        clearable
        class="showcase-cat"
        background-color="rgba(0,150,136,0.06)"
      ></v-select>

      <v-select
        v-model="sort"
        :items="sortOptions"
        item-text="label"
        item-value="key"
        label="Sort"
        solo flat hide-details dense
        class="showcase-sort"
        background-color="rgba(0,150,136,0.06)"
      ></v-select>
    </div>

    <!-- ── Loading ── -->
    <div v-if="loading" class="showcase-loading">
      <v-progress-circular indeterminate size="40" color="teal"></v-progress-circular>
    </div>

    <!-- ── Empty ── -->
    <div v-else-if="displayedProducts.length === 0" class="showcase-empty">
      <v-icon size="60" color="grey lighten-1">inventory_2</v-icon>
      <p>No products found</p>
      <v-btn small text color="teal" @click="loadProducts" class="text-none font-weight-bold">Refresh</v-btn>
    </div>

    <!-- ── Grid ── -->
    <div v-else class="showcase-grid" :class="{ 'showcase-grid-compact': selectedIds.length > 0 }">
      <div
        v-for="p in displayedProducts"
        :key="p.id"
        class="showcase-card"
        :class="{ selected: isSelected(p.id), highlighted: isHighlighted(p.id) }"
        @click="openDetail(p)"
      >
        <div class="showcase-check" v-if="selectedIds.length || true" @click.stop="toggleSelected(p.id)">
          <v-icon :color="isSelected(p.id) ? 'teal' : 'grey lighten-1'">
            {{ isSelected(p.id) ? 'check_circle' : 'radio_button_unchecked' }}
          </v-icon>
        </div>

        <div class="showcase-img">
          <img v-if="p.image" :src="p.image" :alt="p.name" loading="lazy">
          <div v-else class="showcase-img-placeholder"><v-icon size="42">shopping_bag</v-icon></div>
          <span class="showcase-stock" :class="p.in_stock ? 'in' : 'out'">
            {{ p.in_stock ? 'In stock' : 'Out of stock' }}
          </span>
          <span v-if="p.quantity !== undefined && p.quantity !== null" class="showcase-qty">
            {{ p.quantity }} left
          </span>
        </div>

        <div class="showcase-card-body">
          <span v-if="p.category" class="showcase-cat-chip">{{ p.category }}</span>
          <h3 class="showcase-name" :title="p.name">{{ p.name }}</h3>
          <p v-if="cleanDescription(p.description)" class="showcase-desc">{{ cleanDescription(p.description) }}</p>
          <div class="showcase-card-foot">
            <span class="showcase-price">{{ formatAmount(p.price) }}</span>
            <div class="showcase-card-actions" @click.stop>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" color="teal" @click="openDetail(p)">
                    <v-icon size="18">visibility</v-icon>
                  </v-btn>
                </template>
                <span>View details</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn icon small v-on="on" color="primary" @click="goToManagement(p)">
                    <v-icon size="17">edit</v-icon>
                  </v-btn>
                </template>
                <span>Edit / manage</span>
              </v-tooltip>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Detail dialog ── -->
    <v-dialog
      v-model="detailDialog"
      max-width="900px"
      :fullscreen="$vuetify.breakpoint.xs"
      content-class="showcase-detail-dialog"
      :retain-focus="false"
      transition="dialog-bottom-transition"
    >
      <v-card v-if="active" class="showcase-detail-card">
        <div class="showcase-detail-head" :style="{ background: detailGradient }">
          <div class="showcase-detail-brand">
            <div class="showcase-detail-icon"><v-icon color="white">local_offer</v-icon></div>
            <div>
              <div class="showcase-detail-title">{{ active.name }}</div>
              <div class="showcase-detail-sub">{{ active.category || 'Marketplace' }} &middot; #{{ active.id }}</div>
            </div>
          </div>
          <v-btn icon dark @click="detailDialog = false"><v-icon>close</v-icon></v-btn>
        </div>

        <div class="showcase-detail-body">
          <div class="showcase-detail-left">
            <v-carousel
              v-if="detailMedia.length"
              height="300"
              hide-delimiter-background
              show-arrows-on-hover
            >
              <v-carousel-item v-for="(img, i) in detailMedia" :key="i" contain>
                <div class="showcase-detail-media">
                  <img :src="img" :alt="active.name">
                </div>
              </v-carousel-item>
            </v-carousel>
            <div v-else class="showcase-detail-media showcase-detail-media-empty">
              <v-icon size="60">image_outlined</v-icon>
              <p>No image</p>
            </div>
          </div>

          <div class="showcase-detail-right">
            <div class="showcase-detail-price">{{ formatAmount(active.price) }}</div>

            <div class="showcase-detail-specs">
              <div class="spec-row">
                <span class="spec-label"><v-icon x-small>category</v-icon> Category</span>
                <span class="spec-value">{{ active.category || '—' }}</span>
              </div>
              <div class="spec-row">
                <span class="spec-label"><v-icon x-small>inventory_2</v-icon> Stock</span>
                <span class="spec-value" :class="active.in_stock ? 'green--text' : 'red--text'">
                  {{ active.in_stock ? 'In stock' : 'Out of stock' }}
                </span>
              </div>
              <div class="spec-row" v-if="active.quantity !== undefined && active.quantity !== null">
                <span class="spec-label"><v-icon x-small>numbers</v-icon> Quantity</span>
                <span class="spec-value">{{ active.quantity }}</span>
              </div>
              <div class="spec-row">
                <span class="spec-label"><v-icon x-small>tag</v-icon> Product ID</span>
                <span class="spec-value">#{{ active.id }}</span>
              </div>
            </div>

            <div class="showcase-detail-desc" v-if="cleanDescription(active.description)">
              <h4>Description</h4>
              <div class="showcase-desc-html black--text" v-html="active.description"></div>
            </div>

            <div class="showcase-detail-actions">
              <v-btn
                color="teal" dark depressed small class="text-none font-weight-bold"
                @click="toggleSelected(active.id)"
              >
                <v-icon left size="15">{{ isSelected(active.id) ? 'check_circle' : 'add_circle_outline' }}</v-icon>
                {{ isSelected(active.id) ? 'Selected' : 'Select product' }}
              </v-btn>
              <v-btn
                color="primary" dark depressed small class="text-none font-weight-bold"
                @click="goToManagement(active)"
              >
                <v-icon left size="15">edit</v-icon>
                Manage
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ProductShowcase',
  data() {
    return {
      products: [],
      categories: [],
      loading: false,
      search: '',
      category: '',
      sort: 'recent',
      sortOptions: [
        { label: 'Sort by price ↑', key: 'price-asc' },
        { label: 'Sort by price ↓', key: 'price-desc' },
        { label: 'Alphabetical', key: 'name' },
        { label: 'Recently added', key: 'recent' }
      ],
      selectedIds: [],
      detailDialog: false,
      active: null,
      highlightId: null,
      detailImages: []
    }
  },
  computed: {
    allSelected() {
      return this.displayedProducts.length > 0 && this.selectedIds.length === this.displayedProducts.length
    },
    someSelected() {
      return this.selectedIds.length > 0 && !this.allSelected
    },
    filteredProducts() {
      const q = this.search.trim().toLowerCase()
      let list = this.products
      if (q) list = list.filter(p => (p.name || '').toLowerCase().includes(q))
      if (this.category) list = list.filter(p => (p.category || '') === this.category)
      if (this.sort === 'price-asc') list = list.slice().sort((a, b) => (a.price || 0) - (b.price || 0))
      else if (this.sort === 'price-desc') list = list.slice().sort((a, b) => (b.price || 0) - (a.price || 0))
      else if (this.sort === 'name') list = list.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      return list
    },
    displayedProducts() {
      return this.filteredProducts
    },
    detailGradient() {
      return 'linear-gradient(135deg, #00897b 0%, #00695c 100%)'
    },
    detailMedia() {
      const media = []
      if (this.active && this.active.image) media.push(this.active.image)
      if (this.active && this.active.thumbnail && this.active.thumbnail !== this.active.image) media.push(this.active.thumbnail)
      return media
    }
  },
  watch: {
    '$route.query.highlight'(id) {
      this.applyHighlight(id)
    }
  },
  mounted() {
    this.loadCategories()
    this.loadProducts()
    document.title = 'PettyCash: Manager | Product Showcase'
  },
  methods: {
    async loadCategories() {
      try {
        const res = await axios.get('/api/v1/manager/market_place/categories/')
        this.categories = (res.data || [])
          .map(c => (typeof c === 'string' ? { name: c } : c))
          .filter(c => c && c.name)
      } catch (e) {
        console.error('Error loading categories:', e)
      }
    },
    async loadProducts() {
      this.loading = true
      try {
        const res = await axios.get('/api/v1/manager/market_place/products/browse/')
        this.products = (res.data && res.data.products) || []
        this.applyHighlight(this.$route.query.highlight)
      } catch (error) {
        console.error('Error loading products:', error)
        this.snack('Failed to load products', 'red darken-4')
      } finally {
        this.loading = false
      }
    },
    applyHighlight(id) {
      if (!id) return
      this.highlightId = Number(id)
      const found = this.products.find(p => p.id === this.highlightId)
      if (found) {
        this.openDetail(found)
      }
    },
    isHighlighted(id) {
      return this.highlightId && this.highlightId === Number(id)
    },
    openDetail(p) {
      this.active = p
      this.detailDialog = true
    },
    goToManagement(p) {
      this.detailDialog = false
      this.$router.push('/market_place/products/detail/' + p.id + '/')
    },
    isSelected(id) {
      return this.selectedIds.includes(Number(id))
    },
    toggleSelected(id) {
      id = Number(id)
      const i = this.selectedIds.indexOf(id)
      if (i === -1) this.selectedIds.push(id)
      else this.selectedIds.splice(i, 1)
    },
    toggleAllSelected(value) {
      if (value) this.selectedIds = this.displayedProducts.map(p => Number(p.id))
      else this.selectedIds = []
    },
    selectedProducts() {
      return this.products.filter(p => this.selectedIds.includes(Number(p.id)))
    },
    shareSelected() {
      const list = this.selectedProducts()
      if (!list.length) return
      const text = 'PettyCash Marketplace products:\n' + list.map(p => '• ' + p.name + ' — ' + this.formatAmount(p.price)).join('\n')
      const url = 'https://wa.me/?text=' + encodeURIComponent(text)
      window.open(url, '_blank')
    },
    async copySelected() {
      const list = this.selectedProducts()
      if (!list.length) return
      const text = list.map(p => '• ' + p.name + ' — ' + this.formatAmount(p.price)).join('\n')
      try {
        await navigator.clipboard.writeText(text)
        this.snack('Copied ' + list.length + ' product(s)', 'teal darken-1')
      } catch (e) {
        this.snack('Could not copy', 'red darken-4')
      }
    },
    cleanDescription(html) {
      if (!html) return ''
      const div = document.createElement('div')
      div.innerHTML = html
      const text = div.textContent || div.innerText || ''
      const clean = text.replace(/\s+/g, ' ').trim()
      return clean.length > 90 ? clean.substring(0, 90) + '…' : clean
    },
    formatAmount(amount) {
      if (!amount && amount !== 0) return ''
      return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA'
    },
    snack(message, color) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color || 'teal darken-1')
      this.$store.commit('activateSnackBar', true)
    }
  }
}
</script>

<style scoped>
.product-showcase {
  max-width: 1280px;
  margin: 0 auto;
  padding: 16px;
}

/* ── Hero ── */
.showcase-hero {
  background: linear-gradient(135deg, #26a69a 0%, #00695c 100%);
  border-radius: 20px;
  padding: 26px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 16px;
  box-shadow: 0 10px 30px rgba(0, 105, 92, 0.25);
}

.showcase-hero-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.showcase-hero-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
}

.showcase-title {
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  margin: 0;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.showcase-sub {
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.92);
  margin: 4px 0 0;
}

.showcase-hero-actions {
  display: flex;
  gap: 8px;
}

/* ── Toolbar ── */
.showcase-toolbar {
  background: rgba(0, 150, 136, 0.08);
  border: 1px solid rgba(0, 150, 136, 0.25);
  border-radius: 14px;
  padding: 8px 16px;
  margin-bottom: 14px;
}

/* ── Filters ── */
.showcase-filters {
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.showcase-search {
  flex: 1;
  min-width: 200px;
}

.showcase-cat {
  width: 210px;
}

.showcase-sort {
  width: 200px;
}

/* ── Loading / empty ── */
.showcase-loading,
.showcase-empty {
  text-align: center;
  padding: 60px 10px;
  color: rgba(0, 0, 0, 0.4);
}

.showcase-empty p {
  margin: 10px 0;
  font-size: 14px;
}

/* ── Grid ── */
.showcase-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 16px;
}

.showcase-card {
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(0, 150, 136, 0.14);
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.25s ease;
  position: relative;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.showcase-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 14px 30px rgba(0, 105, 92, 0.18);
  border-color: rgba(0, 150, 136, 0.35);
}

.showcase-card.selected {
  border-color: #00897b;
  box-shadow: 0 4px 18px rgba(0, 150, 136, 0.28);
}

.showcase-card.highlighted {
  border-color: #00897b;
  box-shadow: 0 0 0 3px rgba(0, 150, 136, 0.35), 0 14px 30px rgba(0, 105, 92, 0.18);
}

.showcase-check {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 3;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.showcase-img {
  height: 165px;
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.1), rgba(0, 121, 107, 0.14));
  position: relative;
  overflow: hidden;
}

.showcase-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.showcase-img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.25);
}

.showcase-stock {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 10.5px;
  font-weight: 800;
  padding: 3px 9px;
  border-radius: 12px;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.showcase-stock.in {
  background: #16a34a;
}

.showcase-stock.out {
  background: #dc2626;
}

.showcase-qty {
  position: absolute;
  bottom: 10px;
  left: 10px;
  font-size: 10.5px;
  font-weight: 700;
  color: #212121;
  background: rgba(255, 255, 255, 0.9);
  padding: 2px 8px;
  border-radius: 10px;
}

.showcase-card-body {
  padding: 12px 14px 14px;
}

.showcase-cat-chip {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  color: #4f63d6;
  background: rgba(102, 126, 234, 0.1);
  padding: 2px 8px;
  border-radius: 8px;
  margin-bottom: 6px;
}

.showcase-name {
  font-size: 15px;
  font-weight: 700;
  color: #212121;
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.showcase-desc {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
  margin: 0 0 8px;
  line-height: 1.4;
}

.showcase-card-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.showcase-price {
  font-size: 15px;
  font-weight: 800;
  color: #00897b;
}

.showcase-card-actions {
  display: flex;
  gap: 2px;
}

/* ── Detail dialog ── */
.showcase-detail-card {
  background: rgba(255, 255, 255, 0.98) !important;
  border-radius: 20px !important;
  overflow: hidden;
  color: #212121;
  border: 1px solid rgba(0, 150, 136, 0.2);
}

.showcase-detail-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 22px;
  flex-shrink: 0;
}

.showcase-detail-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.showcase-detail-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.showcase-detail-title {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60vw;
}

.showcase-detail-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.9);
}

.showcase-detail-body {
  display: flex;
  gap: 22px;
  padding: 22px;
  flex-wrap: wrap;
}

.showcase-detail-left {
  flex: 1 1 340px;
  min-width: 280px;
}

.showcase-detail-media {
  width: 100%;
  height: 300px;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(0, 150, 136, 0.1), rgba(0, 121, 107, 0.14));
}

.showcase-detail-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.showcase-detail-media-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.25);
}

.showcase-detail-media-empty p {
  margin: 8px 0 0;
  font-size: 13px;
}

.showcase-detail-right {
  flex: 1 1 320px;
  min-width: 260px;
  display: flex;
  flex-direction: column;
}

.showcase-detail-price {
  font-size: 30px;
  font-weight: 800;
  color: #00897b;
  margin-bottom: 14px;
}

.showcase-detail-specs {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 14px;
}

.spec-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 9px 14px;
  font-size: 13px;
}

.spec-row:nth-child(odd) {
  background: rgba(0, 0, 0, 0.03);
}

.spec-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: rgba(0, 0, 0, 0.55);
  font-weight: 600;
}

.spec-value {
  font-weight: 700;
  color: #212121;
}

.showcase-detail-desc {
  margin-bottom: 16px;
}

.showcase-detail-desc h4 {
  font-size: 13px;
  font-weight: 800;
  color: rgba(0, 0, 0, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin: 0 0 6px;
}

.showcase-desc-html {
  font-size: 13px;
  line-height: 1.55;
  max-height: 180px;
  overflow-y: auto;
}

.showcase-detail-actions {
  display: flex;
  gap: 10px;
  margin-top: auto;
  flex-wrap: wrap;
}

/* ── Mobile ── */
@media (max-width: 600px) {
  .product-showcase {
    padding: 10px;
  }

  .showcase-hero {
    padding: 18px;
  }

  .showcase-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .showcase-img {
    height: 120px;
  }

  .showcase-card-body {
    padding: 9px 10px 12px;
  }

  .showcase-card-actions {
    display: none;
  }

  .showcase-detail-body {
    padding: 16px;
  }

  .showcase-detail-media {
    height: 220px;
  }
}
</style>